// WHY (#1448): STANDARD-only weight-and-dimensions block. The redesigned
//   STANDARD form drops the per-item Positions editor and captures the
//   request-level total weight (kg) + volume (m³) directly here. Both
//   inputs use the #1437 decimal-safe text buffer so an in-progress
//   fractional value (`0.75`, `12.5`) persists without collapsing.
//
// @aiPurpose Edit the STANDARD request-level `totalWeight` / `totalVolume`
//   that map to the #1447 wire `weight` / `volume` draft-save fields.

import { useTranslation } from 'react-i18next';
import { Eyebrow, Input } from '../../../ui/index.js';
import { useRequestsStore } from '../store.js';
import { Card, Field, Grid2 } from './DraftEditorPrimitives.js';
import { useDecimalTextInput } from './useDecimalTextInput.js';
import type { DraftScratchpad } from '../types.js';

export function DraftStandardTotalsCard({ d }: { d: DraftScratchpad }) {
  const { t } = useTranslation(['requests']);
  const patchHeader = useRequestsStore((s) => s.patchDraftHeader);
  // WHY (#1437): bind the local decimal buffer to the store value via the
  //   shared hook — NEVER bind the Input directly to a parsed number.
  const weightField = useDecimalTextInput(d.totalWeight, (totalWeight) =>
    patchHeader({ totalWeight }),
  );
  const volumeField = useDecimalTextInput(d.totalVolume, (totalVolume) =>
    patchHeader({ totalVolume }),
  );
  return (
    <Card>
      <div data-testid="draft-standard-totals-card">
        <Eyebrow>{t('requests:draft.standardTotals.section')}</Eyebrow>
        <div style={{ height: 12 }} />
        <Grid2>
          <Field label={t('requests:draft.standardTotals.weight')} required>
            <Input
              monospace
              value={weightField.text}
              onChange={weightField.onChange}
              onBlur={weightField.onBlur}
              placeholder="0"
              ariaLabel={t('requests:draft.standardTotals.weight')}
            />
          </Field>
          <Field label={t('requests:draft.standardTotals.volume')} required>
            <Input
              monospace
              value={volumeField.text}
              onChange={volumeField.onChange}
              onBlur={volumeField.onBlur}
              placeholder="0"
              ariaLabel={t('requests:draft.standardTotals.volume')}
            />
          </Field>
        </Grid2>
      </div>
    </Card>
  );
}
